import React from 'react';
import { Box, Typography, Select, MenuItem, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { uploadStageFile, listStageFiles, downloadStageFile, deleteStageFile, downloadFileAsBlob } from '../services/fileService';
import api from '../services/api';

interface WorkflowFilesProps {
  agencySlug: string;
  propertyId: string;
  workflows: Array<{ _id: string; name: string; stages: Array<{ name: string; tasks?: Array<{ name: string; taskType?: string }> }>; currentStage?: number; isActive?: boolean; status?: string; type?: string }>;
  property?: { agencyAgreement?: { fileName: string; url: string; key: string; uploadedAt: string; size?: number }; rentalManagementAgreement?: { fileName: string; url?: string; key: string; uploadedAt: string; size?: number }; contract?: { fileName: string; url: string; key: string; uploadedAt: string; type?: string }; floorPlans?: Array<{ fileName: string; url: string; key: string; uploadedAt: string; size?: number }>; status?: string };
  onChange?: () => void;
  refreshKey?: number;
}

const FILE_TYPE_OPTIONS = [
  { value: 'agent_agreement', label: 'Agency Agreement' },
  { value: 'auction_contract', label: 'Auction Contract' },
  { value: 'pre_auction_contract', label: 'Pre-Auction Contract' },
  { value: 'sale_contract', label: 'Sale Contract' },
  { value: 'rental_contract', label: 'Rental Contract' },
  { value: 'staging_contract', label: 'Staging Contract' },
  { value: 'fixup_contract', label: 'Property Fixup Contract' },
  { value: 'photography_contract', label: 'Photography Contract' },
  { value: 'cleaning_contract', label: 'Cleaning Contract' },
  { value: 'consultation_report', label: 'Consultation Report' },
  { value: 'inspection_report', label: 'Inspection Report' },
  { value: 'market_analysis', label: 'Market Analysis' },
  { value: 'valuation_report', label: 'Valuation Report' },
  { value: 'strata_report', label: 'Strata Report' },
  { value: 'building_report', label: 'Building Report' },
  { value: 'da_approval', label: 'DA Approval' },
  { value: 'pest_report', label: 'Pest Report' },
  { value: 'building_pest_report', label: 'Building & Pest Report' },
  { value: 'information_sheet', label: 'Information Sheet' },
  { value: 'rp_data', label: 'RP Data' },
  { value: 'document', label: 'Document' },
  { value: 'image', label: 'Image' },
  { value: 'other', label: 'Other' },
];

const WorkflowFiles: React.FC<WorkflowFilesProps> = ({ agencySlug, propertyId, workflows, property, onChange, refreshKey }) => {
  const workflowsKey = React.useMemo(
    () => (workflows || []).map(w => w?._id).filter(Boolean).sort().join(','),
    [workflows]
  );
  const propertyFilesKey = React.useMemo(
    () => [
      property?.agencyAgreement?.key,
      property?.rentalManagementAgreement?.key,
      property?.contract?.key,
      ...(property?.floorPlans?.map(fp => fp.key) || [])
    ].filter(Boolean).join('|'),
    [property?.agencyAgreement?.key, property?.rentalManagementAgreement?.key, property?.contract?.key, ...(property?.floorPlans?.map(fp => fp.key) || [])]
  );
  const getDefaultStage = React.useCallback(() => {
    const active = (workflows || []).find(w => w.isActive) || (workflows || [])[0];
    if (!active) return null;
    const stageIndex = typeof active.currentStage === 'number' ? active.currentStage : 0;
    return { workflowId: active._id, stageIndex };
  }, [workflows]);
  const [selectedStage, setSelectedStage] = React.useState<{ workflowId: string; stageIndex: number } | null>(getDefaultStage());
  React.useEffect(() => { setSelectedStage(getDefaultStage()); }, [getDefaultStage]);
  const [fileType, setFileType] = React.useState<string>('document');
  const [visitorExposable, setVisitorExposable] = React.useState<boolean>(false);
  const [pendingFile, setPendingFile] = React.useState<File | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const [filesByLabel, setFilesByLabel] = React.useState<Record<string, Array<{ file: any; stageIndex: number; workflowId: string }>>>({});
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [deleteDialog, setDeleteDialog] = React.useState<{ open: boolean; file?: any; stageIndex?: number; workflowId?: string }>(() => ({ open: false }));
  const [contractorDeleteDialog, setContractorDeleteDialog] = React.useState<{ open: boolean; file?: any }>(() => ({ open: false }));
  const [contractorJobGroups, setContractorJobGroups] = React.useState<Array<{ stageName: string; files: any[] }>>([]);

  const refresh = async () => {
    const typeLabel = (t?: string) => FILE_TYPE_OPTIONS.find(o => o.value === t)?.label || 'Document';
    const grouped: Record<string, Array<{ file: any; stageIndex: number; workflowId: string }>> = {};

    let data: any[] = [];
    try {
      const res = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/files-by-stage`);
      data = Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      console.warn('Failed to load files-by-stage', err);
    }

    // Merge workflow stage files from BOTH sources - files-by-stage (fresh) and workflows prop
    // This ensures RP data and other files show up regardless of which source has them
    const contractorAndReport = data.filter((g: any) => g?.isContractorJob || g?.isReport);
    const fromFilesByStage = data.filter((g: any) => !g?.isContractorJob && !g?.isReport);
    const seenFileIds = new Set<string>();

    const addGroupFiles = (groups: any[]) => {
      for (const g of groups) {
        const list = Array.isArray(g.files) ? g.files : [];
        for (const f of list) {
          const id = f?._id?.toString?.() || f?.key;
          if (id) seenFileIds.add(id);
        }
      }
    };

    // Start with files-by-stage groups
    let workflowGroups: any[] = [];
    for (const g of fromFilesByStage) {
      const files = Array.isArray(g.files) ? g.files : [];
      if (files.length > 0) {
        workflowGroups.push({
          workflowName: g.workflowName,
          stageName: g.stageName,
          order: g.order ?? 0,
          files: files.map((f: any) => (f?.type === 'rp_data' && !f.fileType ? { ...f, fileType: 'valuation' } : f)),
          workflowId: g.workflowId,
          stageIndex: g.stageIndex ?? -1,
        });
        addGroupFiles([{ files }]);
      }
    }

    // Add from workflows prop (may have files when files-by-stage doesn't)
    if (workflows && workflows.length > 0) {
      for (const wf of workflows as any[]) {
        if (!wf?.stages) continue;
        for (let i = 0; i < wf.stages.length; i++) {
          const stage = wf.stages[i];
          const files = Array.isArray(stage?.files) ? stage.files : [];
          const newFiles = files.filter((f: any) => {
            const id = f?._id?.toString?.() || f?.key;
            return id && !seenFileIds.has(id);
          });
          if (newFiles.length > 0) {
            newFiles.forEach((f: any) => {
              const id = f?._id?.toString?.() || f?.key;
              if (id) seenFileIds.add(id);
            });
            workflowGroups.push({
              workflowName: wf.name || 'Workflow',
              stageName: stage.name || `Stage ${i}`,
              order: stage.order ?? i,
              files: newFiles.map((f: any) => (f?.type === 'rp_data' && !f.fileType ? { ...f, fileType: 'valuation' } : f)),
              workflowId: wf._id,
              stageIndex: i,
            });
          }
        }
      }
    }

    // Add RP data from property_valuation task fieldValues (same source as Property Valuation task UI)
    if (workflows && workflows.length > 0) {
      for (const wf of workflows as any[]) {
        if (!wf?.stages) continue;
        for (let stageIdx = 0; stageIdx < wf.stages.length; stageIdx++) {
          const stage = wf.stages[stageIdx];
          const tasks = Array.isArray(stage?.tasks) ? stage.tasks : [];
          for (const task of tasks) {
            if ((task.taskType !== 'property_valuation' && task.name !== 'Property Valuation') || !task.fieldValues?.rpDataFiles) continue;
            const rpFiles = Array.isArray(task.fieldValues.rpDataFiles) ? task.fieldValues.rpDataFiles : [];
            for (const rf of rpFiles) {
              const fileId = rf.fileId || rf._id;
              if (!fileId || !rf.fileName) continue;
              if (seenFileIds.has(String(fileId))) continue;
              seenFileIds.add(String(fileId));
              workflowGroups.push({
                workflowName: wf.name || 'Workflow',
                stageName: stage.name || `Stage ${stageIdx}`,
                order: stage.order ?? stageIdx,
                files: [{
                  _id: fileId,
                  fileName: rf.fileName,
                  fileType: rf.fileType,
                  type: 'rp_data',
                }],
                workflowId: wf._id,
                stageIndex: stageIdx,
              });
            }
          }
        }
      }
    }

    data = [...workflowGroups, ...contractorAndReport];

    const contractorGroups = data
      .filter((g: any) => g && g.isContractorJob && Array.isArray(g.files) && g.files.length > 0)
      .map((g: any) => ({ stageName: g.stageName, files: g.files }));
    setContractorJobGroups(contractorGroups);

    data
      .filter((g: any) => g && !g.isContractorJob)
      .forEach((group: any) => {
        const list = Array.isArray(group.files) ? group.files : [];
        if (!list.length) return;

        list.forEach((f: any) => {
          // Note: Agency agreements can be in both workflow stage files AND property.agencyAgreement
          // Show them in the Files section so users can see them
          // Normalize: handle both fileName and filename (API may use either)
          const displayName = f.fileName || f.filename || '';
          // Normalize type: workflows API or files-by-stage may omit type; infer rp_data from fileType or filename
          const fn = displayName.toLowerCase();
          const isRPData = f.type === 'rp_data' || f.fileType === 'valuation' || f.fileType === 'cma' || fn.includes('valuation') || fn.includes('cma');
          const effectiveType = isRPData ? 'rp_data' : (f.type || f.fileType || 'document');
          const label = typeLabel(effectiveType);
          const isConsultationReport = f.source === 'consultation-report' || f._id === 'consultation-report' || (group.isReport && effectiveType === 'report');
          const enriched = {
            ...f,
            fileName: displayName || f.fileName || f.filename,
            type: effectiveType,
            workflowName: group.workflowName,
            stageName: group.stageName,
            ...(isConsultationReport && f.url ? { isPropertyLevel: true } : {}),
          };

          // For RP data files, check if we already have this file (by key or _id) to prevent duplicates
          if (effectiveType === 'rp_data') {
            const existingFile = grouped[label]?.find((entry: any) => {
              const entryFile = entry.file;
              return (entryFile.key && f.key && entryFile.key === f.key) ||
                     (entryFile._id && f._id && String(entryFile._id) === String(f._id));
            });
            if (existingFile) {
              console.log(`[WorkflowFiles] Skipping duplicate RP data file: ${displayName} (already exists)`);
              return;
            }
          }

          (grouped[label] = grouped[label] || []).push({
            file: enriched,
            stageIndex: group.stageIndex ?? -1,
            workflowId: group.workflowId || '',
          });
        });
      });

    // Collect all contract file keys from workflow stages to avoid duplication
    const workflowContractKeys = new Set<string>();
    Object.values(grouped).forEach(entries => {
      entries.forEach(({ file }) => {
        if (file.type === 'sale_contract' || file.type === 'rental_contract' || file.type === 'auction_contract' || file.type === 'pre_auction_contract') {
          if (file.key) workflowContractKeys.add(file.key);
          if (file.fileName) workflowContractKeys.add(file.fileName);
        }
      });
    });

    // Add property-level files (agency agreement, contract, floor plan)
    if (property) {
      console.log('WorkflowFiles: Property data:', { 
        hasAgencyAgreement: !!property.agencyAgreement, 
        agencyAgreement: property.agencyAgreement 
      });
      if (property.agencyAgreement && property.agencyAgreement.fileName) {
        const label = 'Agency Agreement';
        const enriched = {
          _id: `agency-agreement-${propertyId}`,
          fileName: property.agencyAgreement.fileName,
          url: property.agencyAgreement.url,
          key: property.agencyAgreement.key,
          uploadedAt: property.agencyAgreement.uploadedAt,
          size: property.agencyAgreement.size,
          type: 'agent_agreement',
          isPropertyLevel: true,
        };
        (grouped[label] = grouped[label] || []).push({
          file: enriched,
          stageIndex: -1,
          workflowId: '',
        });
      }
      if (property.rentalManagementAgreement && property.rentalManagementAgreement.fileName && property.rentalManagementAgreement.key) {
        const label = 'Rental Management Agreement';
        const enriched = {
          _id: `rental-management-agreement-${propertyId}`,
          fileName: property.rentalManagementAgreement.fileName,
          url: property.rentalManagementAgreement.url,
          key: property.rentalManagementAgreement.key,
          uploadedAt: property.rentalManagementAgreement.uploadedAt,
          size: property.rentalManagementAgreement.size,
          type: 'rental_management_agreement',
          isPropertyLevel: true,
        };
        (grouped[label] = grouped[label] || []).push({
          file: enriched,
          stageIndex: -1,
          workflowId: '',
        });
      }
      if (property.contract && property.contract.fileName && !workflowContractKeys.has(property.contract.key || '') && !workflowContractKeys.has(property.contract.fileName || '')) {
        const isRental = property.status === 'rent' || property.status === 'leased';
        const contractType = property.contract.type || (isRental ? 'rental' : 'sale');
        const label = contractType === 'sale' ? 'Sale Contract' : 'Rental Contract';
        const enriched = {
          _id: `contract-${propertyId}`,
          fileName: property.contract.fileName,
          url: property.contract.url,
          key: property.contract.key,
          uploadedAt: property.contract.uploadedAt,
          type: contractType === 'sale' ? 'sale_contract' : 'rental_contract',
          isPropertyLevel: true,
        };
        (grouped[label] = grouped[label] || []).push({
          file: enriched,
          stageIndex: -1,
          workflowId: '',
        });
      }
      if (property.floorPlans && property.floorPlans.length > 0) {
        const label = 'Floor Plan';
        property.floorPlans.forEach((floorPlan, index) => {
          const enriched = {
            _id: `floor-plan-${propertyId}-${index}`,
            fileName: floorPlan.fileName,
            url: floorPlan.url,
            key: floorPlan.key,
            uploadedAt: floorPlan.uploadedAt,
            size: floorPlan.size,
            type: 'floor_plan',
            isPropertyLevel: true,
          };
          (grouped[label] = grouped[label] || []).push({
            file: enriched,
            stageIndex: -1,
            workflowId: '',
          });
        });
      }
    }

    console.log('WorkflowFiles: Grouped files:', grouped);
    console.log('WorkflowFiles: Grouped keys:', Object.keys(grouped));
    setFilesByLabel(grouped);
  };

  const lastRefreshSignature = React.useRef<string>('');
  React.useEffect(() => {
    // Allow refresh when we have agencySlug and propertyId - files-by-stage fetches from DB, not from workflows prop.
    // Previously we required workflowsKey, which blocked refresh when workflows hadn't loaded yet.
    if (!agencySlug || !propertyId) return;
    const signature = `${agencySlug}|${propertyId}|${refreshKey}|${workflowsKey}|${propertyFilesKey}`;
    if (signature === lastRefreshSignature.current) return;
    lastRefreshSignature.current = signature;
    refresh();
  }, [refreshKey, workflowsKey, propertyFilesKey, agencySlug, propertyId]);

  // Listen for fileListRefresh (e.g. after agency agreement upload) so Files section updates without waiting for refreshKey
  React.useEffect(() => {
    const handler = () => {
      if (agencySlug && propertyId) {
        lastRefreshSignature.current = '';
        refresh();
      }
    };
    window.addEventListener('fileListRefresh', handler);
    return () => window.removeEventListener('fileListRefresh', handler);
  }, [agencySlug, propertyId]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    setPendingFile(e.target.files[0]);
    setVisitorExposable(false);
  };

  const handleUpload = async () => {
    if (!pendingFile || !selectedStage) return;
    const file = pendingFile;
    setUploading(true);
    try {
      const isContract = fileType === 'sale_contract' || fileType === 'rental_contract';
      await uploadStageFile(agencySlug, propertyId, selectedStage.stageIndex, file, selectedStage.workflowId, fileType, {
        visitorExposable: isContract ? true : visitorExposable,
      });

      // If RP Data, sync into the property_valuation task's rpDataFiles array
      if (fileType === 'rp_data') {
        try {
          const filesData = await listStageFiles(agencySlug, propertyId, selectedStage.stageIndex, selectedStage.workflowId);
          const list = Array.isArray(filesData) ? filesData : (filesData.files || []);
          const rpFiles = list.filter((f: any) => f.type === 'rp_data').map((f: any) => ({ fileId: f._id, fileName: f.fileName }));
          let wf = (workflows || []).find(w => w._id === selectedStage.workflowId) as any;
          let stageIdx = wf?.stages?.findIndex((st: any) => Array.isArray(st.tasks) && st.tasks.some((t: any) => t.taskType === 'property_valuation' || t.name === 'Property Valuation')) ?? -1;
          if (stageIdx < 0) stageIdx = selectedStage.stageIndex;
          let taskIndex = wf?.stages?.[stageIdx]?.tasks?.findIndex((t: any) => t.taskType === 'property_valuation' || t.name === 'Property Valuation') ?? -1;
          if (taskIndex < 0) {
            const res = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/workflows`);
            wf = (res.data || []).find((w: any) => w._id === selectedStage.workflowId);
            stageIdx = wf?.stages?.findIndex((st: any) => Array.isArray(st.tasks) && st.tasks.some((t: any) => t.taskType === 'property_valuation' || t.name === 'Property Valuation')) ?? selectedStage.stageIndex;
            taskIndex = wf?.stages?.[stageIdx]?.tasks?.findIndex((t: any) => t.taskType === 'property_valuation' || t.name === 'Property Valuation') ?? -1;
          }
          const patchTargets: Array<{ stageIdx: number; taskIdx: number }> = [];
          if (wf?.stages) {
            wf.stages.forEach((st: any, sIdx: number) => {
              const tIdx = Array.isArray(st.tasks) ? st.tasks.findIndex((t: any) => t.taskType === 'property_valuation' || t.name === 'Property Valuation') : -1;
              if (tIdx >= 0) patchTargets.push({ stageIdx: sIdx, taskIdx: tIdx });
            });
          }
          if (patchTargets.length === 0 && taskIndex >= 0) patchTargets.push({ stageIdx, taskIdx: taskIndex });
          if (patchTargets.length > 0) {
            const token = localStorage.getItem('token');
            for (const target of patchTargets) {
              await fetch(`/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${selectedStage.workflowId}/tasks/${target.stageIdx}/${target.taskIdx}`, {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                  ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({ fieldValues: { rpDataFiles: rpFiles } })
              });
            }
          }
        } catch {}
      }

      setPendingFile(null);
      setVisitorExposable(false);
      await refresh();
      if (onChange) onChange();
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Don't return early if there are no workflows - we might still have property-level files
  // if (!workflows || workflows.length === 0) {
  //   return <Typography color="text.secondary">No workflows.</Typography>;
  // }

  const isContractType = fileType === 'sale_contract' || fileType === 'rental_contract';

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <Select
          size="small"
          value={fileType}
          onChange={(e) => {
            setFileType(e.target.value);
            setVisitorExposable(false);
            setPendingFile(null);
          }}
        >
          {FILE_TYPE_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
        <input type="file" ref={fileInputRef} onChange={handleFileSelect} disabled={uploading} />
        {pendingFile && (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <input
                type="checkbox"
                checked={isContractType ? true : visitorExposable}
                onChange={(e) => setVisitorExposable(e.target.checked)}
                disabled={isContractType}
              />
              <Typography variant="body2" component="span">Include in visitor email</Typography>
            </label>
            <Button size="small" variant="contained" onClick={handleUpload} disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
            <Button size="small" onClick={() => { setPendingFile(null); setVisitorExposable(false); if (fileInputRef.current) fileInputRef.current.value = ''; }} disabled={uploading}>
              Clear
            </Button>
          </>
        )}
      </Box>

      {Object.keys(filesByLabel).length === 0 ? (
        <Typography color="text.secondary">No files uploaded.</Typography>
      ) : (
        Object.entries(filesByLabel).sort(([a], [b]) => a.localeCompare(b)).map(([label, entries]) => (
          <Box key={label} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{label}</Typography>
            <ul style={{ paddingLeft: 16, margin: 0 }}>
              {entries.map(({ file: f, stageIndex: idx, workflowId: wid }) => {
                const fileName = f.fileName || f.filename;
                if (!fileName) return null;
                return (
                <li key={f._id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Button size="small" variant="text" onClick={async () => {
                    if (f.isPropertyLevel) {
                      // Prefer backend download (redirect) when we have a key to avoid sending Auth to presigned storage URLs (OCI/S3 can return 400)
                      const downloadUrl = f.key
                        ? `${window.location.origin}/api/agencies/${agencySlug}/files/${encodeURIComponent(f.key)}`
                        : f.url;
                      try {
                        await downloadFileAsBlob(downloadUrl, fileName);
                      } catch (err) {
                        if (f.url) window.open(f.url, '_blank');
                      }
                    } else {
                      const url = downloadStageFile(agencySlug, propertyId, idx, f._id, wid);
                      try { await downloadFileAsBlob(url, fileName); } catch {}
                    }
                  }}>{fileName}</Button>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    Type: {FILE_TYPE_OPTIONS.find(opt => opt.value === (f.type || 'document'))?.label || 'Document'}
                    {f.workflowName && f.stageName ? ` • ${f.workflowName} / ${f.stageName}` : ''}
                    {f.isPropertyLevel ? ' • Property Level' : ''}
                    {(f.type === 'sale_contract' || f.type === 'rental_contract' || f.visitorExposable) && (
                      <Typography component="span" variant="caption" color="success.main" sx={{ ml: 0.5 }}>• In visitor email</Typography>
                    )}
                  </Typography>
                  {(() => {
                    // For agency agreements, only show delete if consultation workflow is not completed
                    if (f.isPropertyLevel && f.type === 'agent_agreement') {
                      const consultationWorkflow = (workflows || []).find((w: any) => w.type === 'consultation');
                      const isConsultationCompleted = consultationWorkflow?.status === 'completed';
                      if (isConsultationCompleted) {
                        return null; // Don't show delete button if consultation workflow is completed
                      }
                      // Show delete button for agency agreements when consultation workflow is not completed
                      return (
                        <Button size="small" color="error" onClick={() => setDeleteDialog({ open: true, file: f, stageIndex: idx, workflowId: wid })}>Delete</Button>
                      );
                    }
                    // For other files, show delete button if not property-level
                    return !f.isPropertyLevel ? (
                      <Button size="small" color="error" onClick={() => setDeleteDialog({ open: true, file: f, stageIndex: idx, workflowId: wid })}>Delete</Button>
                    ) : null;
                  })()}
                </li>
                );
              })}
            </ul>
          </Box>
        ))
      )}

      {/* Contractor job files (read-only) */}
      {contractorJobGroups.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Contractor Jobs</Typography>
          {contractorJobGroups.map((grp, i) => (
            <Box key={`${grp.stageName}-${i}`} sx={{ mb: 2 }}>
              <ul style={{ paddingLeft: 16, margin: 0 }}>
                {grp.files.map((f: any) => (
                  <li key={f._id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Button size="small" variant="text" onClick={async () => {
                      if (!f?._id) return;
                      const url = `/api/agencies/${agencySlug}/files/${f._id}`;
                      try { await downloadFileAsBlob(url, f.fileName || f.filename || 'download'); } catch {}
                    }}>{f.fileName || f.filename}</Button>
                    <Typography variant="caption" color="text.secondary">Type: {f.type}</Typography>
                    <Button size="small" color="error" onClick={() => setContractorDeleteDialog({ open: true, file: f })}>Delete</Button>
                  </li>
                ))}
              </ul>
            </Box>
          ))}
        </Box>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false })} maxWidth="xs" fullWidth>
        <DialogTitle>Delete File</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete {deleteDialog.file?.fileName || 'this file'}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false })} variant="outlined">Cancel</Button>
          <Button color="error" variant="contained" onClick={async () => {
            const f = deleteDialog.file; const idx = deleteDialog.stageIndex as number; const wid = deleteDialog.workflowId as string;
            try {
              // Handle property-level agency agreement deletion
              if (f.isPropertyLevel && f.type === 'agent_agreement') {
                const token = localStorage.getItem('token');
                const response = await fetch(`/api/agencies/${agencySlug}/properties/${propertyId}/agency-agreement`, {
                  method: 'DELETE',
                  headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                  }
                });
                if (!response.ok) {
                  const errorData = await response.json().catch(() => ({ error: 'Failed to delete agency agreement' }));
                  throw new Error(errorData.error || 'Failed to delete agency agreement');
                }
              } else {
                // Regular workflow stage file deletion
                await deleteStageFile(agencySlug, propertyId, idx, f._id, wid);
              }
              if (f.type === 'rp_data') {
                try {
                  const filesData = await listStageFiles(agencySlug, propertyId, idx, wid);
                  const list = Array.isArray(filesData) ? filesData : (filesData.files || []);
                  const rpFiles = list.filter((x: any) => x.type === 'rp_data').map((x: any) => ({ fileId: x._id, fileName: x.fileName }));
                  let wf = (workflows || []).find(w => (w as any)._id === wid) as any;
                  let stageIdx = wf?.stages?.findIndex((st: any) => Array.isArray(st.tasks) && st.tasks.some((t: any) => t.taskType === 'property_valuation' || t.name === 'Property Valuation')) ?? idx;
                  let taskIndex = wf?.stages?.[stageIdx]?.tasks?.findIndex((t: any) => t.taskType === 'property_valuation' || t.name === 'Property Valuation') ?? -1;
                  if (taskIndex < 0) {
                    const res = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/workflows`);
                    wf = (res.data || []).find((w: any) => w._id === wid);
                    stageIdx = wf?.stages?.findIndex((st: any) => Array.isArray(st.tasks) && st.tasks.some((t: any) => t.taskType === 'property_valuation' || t.name === 'Property Valuation')) ?? idx;
                    taskIndex = wf?.stages?.[stageIdx]?.tasks?.findIndex((t: any) => t.taskType === 'property_valuation' || t.name === 'Property Valuation') ?? -1;
                  }
                  const patchTargets: Array<{ stageIdx: number; taskIdx: number }> = [];
                  if (wf?.stages) {
                    wf.stages.forEach((st: any, sIdx: number) => {
                      const tIdx = Array.isArray(st.tasks) ? st.tasks.findIndex((t: any) => t.taskType === 'property_valuation' || t.name === 'Property Valuation') : -1;
                      if (tIdx >= 0) patchTargets.push({ stageIdx: sIdx, taskIdx: tIdx });
                    });
                  }
                  if (patchTargets.length === 0 && taskIndex >= 0) patchTargets.push({ stageIdx, taskIdx: taskIndex });
                  if (patchTargets.length > 0) {
                    const token = localStorage.getItem('token');
                    for (const target of patchTargets) {
                      await fetch(`/api/agencies/${agencySlug}/properties/${propertyId}/workflows/${wid}/tasks/${target.stageIdx}/${target.taskIdx}`, {
                        method: 'PATCH',
                        headers: {
                          'Content-Type': 'application/json',
                          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                        },
                        body: JSON.stringify({ fieldValues: { rpDataFiles: rpFiles } })
                      });
                    }
                  }
                } catch {}
              }
            } finally {
              setDeleteDialog({ open: false });
              await refresh();
              if (onChange) onChange();
            }
          }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Contractor job file delete dialog */}
      <Dialog open={contractorDeleteDialog.open} onClose={() => setContractorDeleteDialog({ open: false })} maxWidth="xs" fullWidth>
        <DialogTitle>Delete File</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete {contractorDeleteDialog.file?.fileName || contractorDeleteDialog.file?.filename || 'this file'}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContractorDeleteDialog({ open: false })} variant="outlined">Cancel</Button>
          <Button color="error" variant="contained" onClick={async () => {
            const f = contractorDeleteDialog.file as any;
            if (!f?._id || !f?.jobId) { setContractorDeleteDialog({ open: false }); return; }
            const typeRaw = f?.type || '';
            const fileType = typeRaw === 'floorPlans' || typeRaw === 'floor_plans' ? 'floor-plan' : typeRaw;
            try {
              const token = localStorage.getItem('token');
              const fileId = f._id || f.filename;
              const resp = await fetch(`/api/agencies/${agencySlug}/properties/${propertyId}/contractor-jobs/${f.jobId}/files/${fileId}?fileType=${encodeURIComponent(fileType)}`, {
                method: 'DELETE',
                headers: {
                  'Content-Type': 'application/json',
                  ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                }
              });
              if (!resp.ok) {
                console.error('Contractor file delete failed', resp.status);
              }
            } catch (e) {
              console.error('Contractor file delete error', e);
            } finally {
              setContractorDeleteDialog({ open: false });
              await refresh();
              if (onChange) onChange();
            }
          }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkflowFiles;


